import { useRef, useState } from 'react';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@zg-rentals/react-query5';
import type { PidAnswers } from './contexts/IdVerificationContext';
import IdVerificationContext, { DEFAULT_OPTIONS } from './contexts/IdVerificationContext';
import { ExitReason, TermSet } from '../../models';
import type { IdvStep, LogData } from '../../models';
import type { LogLevel } from '../../models';
import { WizardProvider } from '@zillow/zrs-rental-applications';
import { useAsyncFn, useLatest } from 'react-use';
import Content from './Content';
import type { IdentityCheckQuestion } from '@zg-rentals/ts-id-verification-client';
import { DialogBody, Grid, ModalDialog, StyledDialog, ToastProvider } from '@zillow/constellation';
import IsIdentifiedAndTermsWrapper from './IsIdentifiedAndTermsWrapper';
import IpAddressWrapper from './IpAddressWrapper';
import type { TrackEvent } from './clickstream/events';
import { formatPidAnswersForReview } from './format/format';
import styled from 'styled-components';
import IdvFooter from './IdvFooter';
import IdvHeader from './IdvHeader';
import type { Term } from '@zg-rentals/ts-id-verification-client';
import PassiveAuthWrapper from './PassiveAuthWrapper';
import LoadingWrapper from './LoadingWrapper';
import { DatarouterError } from '@zg-rentals/web-base';

export function noop() {}

export const ModalWithFullHeightBody = styled(ModalDialog)`
  overflow: hidden;

  > ${StyledDialog} {
    height: 100%;
  }
  ${DialogBody} {
    :after {
      height: 0 !important;
    }
    height: 100%;
  }
`;

interface IdVerificationProps {
  onNotAuthenticated?: () => void;
  onTermAccepted?: (id: string) => void;
  onTermDeclined?: (id: string) => void;
  onKbaStepUp?: () => void;
  onSubmitPidForm?: (answers: PidAnswers) => void;
  onExit: (exitReason: { reason: ExitReason; success: boolean }) => void | Promise<void>;
  onError?: ({ code }: { code: string }) => void;
  onStepChange?: ({ step }: { step: IdvStep }) => void;
  trackEvent?: TrackEvent;
  log?: (logLevel: LogLevel, logData: LogData) => void;
  zgApi?: string;
  termSet?: TermSet;
  id: string;
  pidAnswersForReview?: Omit<PidAnswers, 'dob' | 'ssn'>;
  skipLandingStep?: boolean;
  handleScreeningReports?: boolean;
  reauthenticate?: boolean;
}

/**
 * For a function that may be sync or async, returns the function loading state
 * and a ref with the latest function instance
 */
function useLatestAsyncFn<T extends (...args: Array<never>) => unknown>(fn: T) {
  const [{ loading }, callback] = useAsyncFn(async (...args) => await fn(...(args as Array<never>)), [fn]);
  return [loading, useLatest(callback as unknown as T)] as const;
}

export function IdVerification({
  onNotAuthenticated = noop,
  onTermAccepted = noop,
  onTermDeclined = noop,
  onKbaStepUp = noop,
  onSubmitPidForm = noop,
  onExit = noop,
  onError = noop,
  onStepChange = noop,
  trackEvent = noop,
  log = noop,
  termSet = TermSet.RenterScreening,
  zgApi = DEFAULT_OPTIONS.zgApi,
  id = DEFAULT_OPTIONS.id,
  pidAnswersForReview = {
    firstName: '',
    middleName: '',
    lastName: '',
    currentAddress: '',
    currentCity: '',
    currentState: '',
    currentZip: '',
    phone: '',
  },
  skipLandingStep = false,
  handleScreeningReports = false,
  reauthenticate = false,
}: IdVerificationProps) {
  const handleAuthError = async (error: unknown) => {
    if (DatarouterError.is(error) && error.responseStatus === 401) {
      setLoggedIn(false);
      await onNotAuthenticated();
      await onExit({ reason: ExitReason.NotAuthenticated, success: false });
    }
  };
  const queryClient = useRef(
    new QueryClient({
      defaultOptions: {
        queries: {
          retry: false,
          refetchOnWindowFocus: false,
        },
      },
      queryCache: new QueryCache({
        onError: handleAuthError,
      }),
      mutationCache: new MutationCache({
        onError: handleAuthError,
      }),
    }),
  );

  const latestOnNotAuthenticated = useLatest(onNotAuthenticated);
  const latestOnTermAccepted = useLatest(onTermAccepted);
  const latestOnTermDeclined = useLatest(onTermDeclined);
  const latestOnKbaStepUp = useLatest(onKbaStepUp);
  const latestOnSubmitPidForm = useLatest(onSubmitPidForm);
  const [isOnExitLoading, latestOnExit] = useLatestAsyncFn(onExit);
  const latestOnError = useLatest(onError);
  const latestOnStepChange = useLatest(onStepChange);
  const latestTrackEvent = useLatest(trackEvent);
  const latestLog = useLatest(log);
  const [kiqQuestionSet, setKiqQuestionSet] = useState<Array<IdentityCheckQuestion>>([]);
  const [pidAnswers, setPidAnswers] = useState({
    ...formatPidAnswersForReview(pidAnswersForReview),
  });
  const [loading, setLoading] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [pidRemainingAttempts, setPidRemainingAttempts] = useState<number | null>(null);
  const [passiveAuthData, setPassiveAuthData] = useState<Record<string, string>>({
    jsc: '',
    hdimPayload: '',
  });
  const [terms, setTerms] = useState<Array<Term>>([]);
  const [skipTermsStep, setSkipTermsStep] = useState(false);
  const [reauthenticating, setReauthenticating] = useState(reauthenticate);

  return (
    <IdVerificationContext.Provider
      value={{
        ...DEFAULT_OPTIONS,
        zgApi,
        termSet,
        id,
        latestOnNotAuthenticated,
        latestOnTermAccepted,
        latestOnTermDeclined,
        latestOnKbaStepUp,
        latestOnSubmitPidForm,
        latestOnExit,
        latestOnError,
        latestOnStepChange,
        latestTrackEvent,
        latestLog,
        kiqQuestionSet,
        setKiqQuestionSet,
        pidAnswers,
        setPidAnswers,
        loggedIn,
        setLoggedIn,
        pidRemainingAttempts,
        setPidRemainingAttempts,
        passiveAuthData,
        setPassiveAuthData,
        terms,
        setTerms,
        skipTermsStep,
        setSkipTermsStep,
        skipLandingStep,
        handleScreeningReports,
        reauthenticating,
        setReauthenticating,
        isOnExitLoading,
        loading,
        setLoading,
      }}
    >
      <QueryClientProvider client={queryClient.current}>
        <WizardProvider>
          <ModalWithFullHeightBody
            isOpen
            fullScreen
            shouldCloseOnESCKeyPress={false}
            renderCloseButton={null}
            renderHeader={(headerProps: object) => <IdvHeader headerProps={headerProps} />}
            renderBody={
              <ToastProvider>
                <IsIdentifiedAndTermsWrapper>
                  <IpAddressWrapper>
                    <PassiveAuthWrapper>
                      <LoadingWrapper>
                        <Grid display="grid" gridTemplateColumns="repeat(24, 1fr)" style={{ height: '100%' }}>
                          <Grid gridColumnEnd={{ lg_gte: 'span 2' }} display={{ default: 'none', lg_gte: 'block' }} />
                          <Grid display="grid" gridColumnEnd={{ default: 'span 24', lg_gte: 'span 20' }}>
                            <Content />
                          </Grid>
                          <Grid gridColumnEnd={{ lg_gte: 'span 2' }} display={{ default: 'none', lg_gte: 'block' }} />
                        </Grid>
                      </LoadingWrapper>
                    </PassiveAuthWrapper>
                  </IpAddressWrapper>
                </IsIdentifiedAndTermsWrapper>
              </ToastProvider>
            }
            renderFooter={(footerProps: object) => <IdvFooter footerProps={footerProps} />}
          />
        </WizardProvider>
      </QueryClientProvider>
    </IdVerificationContext.Provider>
  );
}
